import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { CircularProgress, Slider, Tooltip } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { CheckboxInput, SelectInput } from '../../../../Helpers/Inputs';
import ErrorHandler from '../../../../Helpers/Errors/ErrorHandler';
import { CollapsibleBasicInfoSummary } from '../../../Common/SubComponents/BasicInfoSummary';
import Age from '../../../../Helpers/Dates/Age';
import IsJuvenile from '../../../../Helpers/Dates/IsJuvenile';
import NoQuoteAvailable from '../../../Common/SubComponents/NoQuoteAvailable';
import PermCoverageAmounts from '../../../../Helpers/Lists/CoverageAmounts/PermCoverageAmounts';
import QuoteCard from '../Helpers/QuoteCard';
import ProductInfoDialog from '../Helpers/ProductInfoDialog';
import ProductDetails from '../Helpers/ProductDetails';
import ProductDetailsJuvi from '../Helpers/ProductDetailsJuvi';
import { 
  SESSION_STORAGE_ACCOUNT_KEY, 
  getSessionStorageItem, 
  SESSION_STORAGE_COVERAGE_AMOUNT_KEY, 
  SESSION_STORAGE_NEEDS_ANALYSIS_KEY, 
  SESSION_STORAGE_ACTIVE_PURL, 
  getBasicInfo, 
  setBasicInfo,
} from '../../../../../sessionStorage';
import QuoteFor from '../../../Common/QuoteFor/QuoteFor';
import BasicInfoSummaryModel from '../../../Common/SubComponents/BasicInfoSummaryModel';

export default function Quoter(props) {
  const [loading, setLoading] = useState(true);
  const [productInfoDialogOpen, setProductInfoDialogOpen] = useState(false);

  const [quoteResults, setQuoteResults] = useState([]);

  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;
  
  const getActivePurlData = getSessionStorageItem(SESSION_STORAGE_ACTIVE_PURL);
  const isActivePurl = getActivePurlData ? getActivePurlData.active : false;
  const basicInfo = getBasicInfo();

  const age = Age(basicInfo.birthdate);
  const isJuvenile = IsJuvenile(basicInfo.birthdate);

  const coverageAmounts = PermCoverageAmounts(isJuvenile);
  const coverageAmountsSliderList = PermCoverageAmounts(isJuvenile, true);

  const [basicInfoModalShow, setBasicInfoModalShow] = useState(false);
  const [isPurlVerification, setIsPurlVerification] = useState(isActivePurl);

  const runQuote = (getIsSubscribed) => {
    const gio = age < 32 && props.formState.gio;

    let queryString = `Birthdate=${props.basicInfo.birthdate || props.basicInfo.fakeBirthdate}&StateCode=${props.basicInfo.state}&Gender=${props.basicInfo.gender}&Face=${props.formState.coverageAmount}&IncludeABA=${props.formState.aba}&IncludeGIO=${gio}`;
    if (isJuvenile === false) {
      queryString += `&Tobacco=${props.basicInfo.tobacco}`;
    }

    fetch(
      `${covrServicesUrl}/api/MinnSPWL?${queryString}`
      , {
        method: 'GET',
        headers: {
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
        },
      })
      .then((res) => res.json())
      .then((json) => {
        if (getIsSubscribed()) {
          json.ageDetermination = 'exact';
          setQuoteResults(json);
          props.onFieldChange('quotedPremium', json.Premium);
          setLoading(false);
        }
      })
      .catch((error) => {
        if (getIsSubscribed()) {
          ErrorHandler(error, setQuoteResults);
          setLoading(false);
        }
      });
  }

  useEffect(() => {
    const coverageValue = sessionStorage.getItem(SESSION_STORAGE_NEEDS_ANALYSIS_KEY);
    if (coverageValue > 0 && coverageValue < 150000) {
      props.onFieldChange('coverageAmount', coverageValue);
    }

    const newValue = sessionStorage.getItem(SESSION_STORAGE_COVERAGE_AMOUNT_KEY);
    if (newValue) {
      props.onFieldChange('coverageAmount', newValue);
    }
  }, []);

  let debounce = null;
  const setQuoteTimer = () => {
    setLoading(true);

    let isSubscribed = true;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    }
  };

  useEffect(() => {
    return setQuoteTimer();
  }, [props.formState.coverageAmount, props.formState.aba, props.formState.gio]);

  const onSubmit = (e) => {
    if (e) {e.preventDefault();}
    const basicInfo = getBasicInfo();
    if (isActivePurl && !basicInfo.birthdate) {
      setIsPurlVerification(true);
      setBasicInfoModalShow(true);
    } else {
      props.onNext(e, props.monthlyRate);
    }
  }

  const handleBasicInfoSubmit = (open, purlMismatch) => {
    setBasicInfoModalShow(open);
    const basicInfo = getBasicInfo();
    setBasicInfo(basicInfo);
    if (isPurlVerification && !purlMismatch) {
      onSubmit();
    } else {
      setQuoteTimer();
    }
  }

  const acceleratedText = `The Accelerated Benefit Agreement is an optional feature with no additional charge and with no effect on premium amount.
  This agreement allows access to a portion of the death benefit coverage in the event of the insured's terminal illness.`;

  const automaticText = `The Automatic Premium Loan Provision (APL provision) is an optional feature with no additional charge and with no effect on premium amount. This provision is included to help protect your contract when premium payments are not paid.
  If a premium is missed, the provision allows a policy loan to be taken from the cash value to pay the premium which will help ensure that the policy will not lapse.`;
  
  return (
    <Container>
      <Row className="quoter">

        <Col lg={4} className="mb-3">
          <Form>
            <h2>Coverage Details</h2>
            <h3>Select Amount</h3>
            <Form.Group className="coverageAmount">
              <SelectInput
                name="coverageAmount"
                id="coverageAmountPerm"
                label="Select Coverage"
                value={props.formState.coverageAmount}
                selectOptions={coverageAmounts}
                onChange={(e) => props.onFieldChange('coverageAmount', e.target.value)}
                variant="outlined"
                hideEmptyOption
              />
            </Form.Group>
            <Form.Group className="ml-3 mr-4">
              <Slider
                min={10000}
                max={isJuvenile ? 75000 : 150000}
                step={null}
                value={props.formState.coverageAmount}
                onChange={(e, v) => props.onFieldChange('coverageAmount', v)}
                className="m-2"
                marks={coverageAmountsSliderList}
              />
            </Form.Group>

            <Form.Group className="checkbox-input">
              {age < 32 &&
                <CheckboxInput
                  id="gio"
                  name="gio"
                  checked={props.formState.gio}
                  onChange={(e) => props.onFieldChange('gio', e.target.checked)}
                  label="Guaranteed Insurability Option (GIO)"
                  helperText="(Check here if you would like the GIO Agreement added to your policy for an additional cost. The option will be equal to the lesser of the coverage you selected or $50,000)"
                  inputRef={() => { }}
                />
              }
              <p>Riders provided at no additional cost to you.</p>

              <div className="d-flex align-items-center checkbox-input">

                <CheckboxInput
                  id="aba"
                  name="aba"
                  checked={props.formState.aba}
                  onChange={(e) => props.onFieldChange('aba', e.target.checked)}
                  label="Accelerated Benefit Agreement"
                  inputRef={() => { }}
                />
                <Tooltip title={acceleratedText} placement="top">
                  <HelpOutlineIcon />
                </Tooltip>
              </div>

              <div className="d-flex align-items-center checkbox-input">
                <CheckboxInput
                  id="aplp"
                  name="aplp"
                  checked={props.formState.aplp}
                  onChange={(e) => props.onFieldChange('aplp', e.target.checked)}
                  label="Automatic Premium Loan Provision"
                  inputRef={() => { }}
                />
                <Tooltip title={automaticText} placement="top">
                  <HelpOutlineIcon />
                </Tooltip>
              </div>
            </Form.Group>

            <CollapsibleBasicInfoSummary
              gender={basicInfo.gender}
              state={basicInfo.state}
              birthdate={basicInfo.birthdate || basicInfo.fakeBirthdate}
              tobacco={basicInfo.tobacco}
              height={basicInfo.height}
              weight={basicInfo.weight}
              showEditLink={true}
              editLinkOnClick={props.onBack}
              isJuvenileApp={true}
            />
          </Form>
        </Col>

        <Col lg={8}>
          <QuoteResult
            coverageAmount={props.formState.coverageAmount}
            result={quoteResults}
            loading={loading}
            setProductInfoDialogOpen={() => setProductInfoDialogOpen(true)}
            onNext={onSubmit}
            isJuvenile={isJuvenile}
          />
        </Col>
      </Row>

      <ProductInfoDialog
        open={productInfoDialogOpen}
        onProductInfoDialogClosed={() => {
          setProductInfoDialogOpen(false);
        }}
      />

      {basicInfoModalShow && 
        <BasicInfoSummaryModel 
          openEditPopUp={basicInfoModalShow}
          closeModal={() => setBasicInfoModalShow(false)} 
          setOpenEditPopUp={handleBasicInfoSubmit}
          isPurlVerification={isPurlVerification}
          product='securianwl'
          juviMode={isJuvenile}
          ageSpecs={props.ageSpecs}
          selectedQuote={quoteResults}
          onFieldChange={props.onFieldChange}
          formState={props.basicInfo} />}
    </Container >
  );
}

function QuoteResult(props) {
  const [expanded, setExpanded] = useState(false);
  if (props.loading) {
    return (
      <div className="loading-container">
        <CircularProgress />
      </div>
    );
  }

  if (!props.result.Premium) {
    return (
      <NoQuoteAvailable />
    );
  }

  const cashValues = [
    props.result.CashValueYr5,
    props.result.CashValueYr10,
    props.result.CashValueYr20,
    props.result.CashValueYr30,
    props.result.CashValueAge65,
  ]

  return (
    <div className="quote-result-section">
      <QuoteFor coverageAmount={props.coverageAmount} productType={props.isJuvenile ? "Children's Whole Life" : "Whole Life"} separator />
      <p className="quote-process">Select continue to complete the entire process online in minutes.</p>

      <QuoteCard
        carrierName="Securian"
        productName="Secure Promise"
        productNameSubtitle={props.isJuvenile ? "Children's Whole Life" : "Whole Life Insurance"}
        monthlyRate={props.result.Premium}
        logoUrl="/logos/securian.png"
        rating="A+"
        productDetails={props.isJuvenile ?
          <ProductDetailsJuvi cashValues={cashValues} setProductInfoDialogOpen={props.setProductInfoDialogOpen} /> :
          <ProductDetails cashValues={cashValues} setProductInfoDialogOpen={props.setProductInfoDialogOpen} />
        }
        expanded={expanded}
        expand={() => setExpanded(!expanded)}
        onClick={props.onNext}
        buttonText="Continue"
      />
    </div>
  );
}

Quoter.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  queryString: PropTypes.object,
};

Quoter.DefaultFormState = (prefill = false, basicInfo, setFaceAmount, isActivePurl) => {
  return {
    coverageAmount: isActivePurl ? setFaceAmount(basicInfo.faceAmount) : 50000,
    quotedPremium: 0,
    gio: false,
    aba: true,
    aplp: true,
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Securian/WL/steps/Quoter.js